"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ModalTypeEnum, useModal } from "@/components/providers/modal-provider/ModalProvider";
import { Calendar } from "@/components/ui/calendar";
import { Input, type InputProps } from "@/components/ui/input";
import { InputA } from "@/components/atoms/Form/FormInput/InputA";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
export type DatePickerCaptionLayout = "label" | "dropdown" | "dropdown-months" | "dropdown-years";
const DEFAULT_START_MONTH = new Date(new Date().getFullYear() - 100, 0);
const DEFAULT_END_MONTH = new Date(new Date().getFullYear() + 50, 11);
type Props = {
  value: Date | undefined;
  onChange: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  disableDates?: (date: Date) => boolean;
  /** Title for the mobile sheet. Ignored on desktop (Popover has no title). */
  title?: string;
  captionLayout?: DatePickerCaptionLayout;
  startMonth?: Date;
  endMonth?: Date;
  /** Render with the shadcn `Input` (true, default) or the legacy `InputA` (false). */
  plainInput?: boolean;
  /** `Input` size — only honoured when `plainInput` (the legacy `InputA` has no size scale). */
  size?: InputProps["size"];
  /** Adds the destructive border ring; used by FormDatePicker when RHF has an error. */
  hasError?: boolean;
  className?: string;
};

/**
 * Read-only text input that opens a `Calendar` for date selection.
 * - Desktop: anchored `Popover` next to the input.
 * - Mobile: bottom `Sheet` via the global `ModalProvider`.
 *
 * Fully controlled via `value` / `onChange` — no form-library dependency.
 * For RHF-bound usage prefer `FormDatePicker`, which is a thin wrapper
 * around this.
 */
export function DatePickerInput(t0) {
  const $ = _c(47);
  const {
    value,
    onChange,
    placeholder: t1,
    disabled: t2,
    disableDates,
    title: t3,
    captionLayout,
    startMonth,
    endMonth,
    plainInput: t4,
    size,
    hasError: t5,
    className
  } = t0;
  const placeholder = t1 === undefined ? "" : t1;
  const disabled = t2 === undefined ? false : t2;
  const title = t3 === undefined ? "Select a date" : t3;
  const plainInput = t4 === undefined ? true : t4;
  const hasError = t5 === undefined ? false : t5;
  const isMobile = useIsMobile();
  const {
    openModal
  } = useModal();
  const [popoverOpen, setPopoverOpen] = useState(false);
  let t6;
  if ($[0] !== value) {
    t6 = value && !isNaN(new Date(value).getTime()) ? format(new Date(value), "MM-dd-yyyy") : "";
    $[0] = value;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const displayValue = t6;
  const TriggerInput = plainInput ? Input : InputA;
  let t7;
  if ($[2] !== plainInput || $[3] !== size) {
    t7 = plainInput ? {
      trailing: <CalendarIcon className="text-muted-foreground" />,
      size
    } : {
      endAdornment: <CalendarIcon size={16} className="text-muted-foreground" />
    };
    $[2] = plainInput;
    $[3] = size;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const trailingProp = t7;
  const t8 = hasError && "border-destructive";
  let t9;
  if ($[5] !== className || $[6] !== t8) {
    t9 = cn("select-none cursor-pointer", t8, className);
    $[5] = className;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== TriggerInput || $[9] !== disabled || $[10] !== displayValue || $[11] !== placeholder || $[12] !== t9 || $[13] !== trailingProp) {
    t10 = <TriggerInput value={displayValue} placeholder={placeholder} type="text" readOnly={true} disabled={disabled} tabIndex={-1} className={t9} {...trailingProp} />;
    $[8] = TriggerInput;
    $[9] = disabled;
    $[10] = displayValue;
    $[11] = placeholder;
    $[12] = t9;
    $[13] = trailingProp;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const triggerInput = t10;
  if (isMobile) {
    let t11;
    if ($[15] !== captionLayout || $[16] !== disableDates || $[17] !== disabled || $[18] !== endMonth || $[19] !== onChange || $[20] !== openModal || $[21] !== startMonth || $[22] !== title || $[23] !== value) {
      t11 = () => {
        if (disabled) {
          return;
        }
        openModal(ModalTypeEnum.GENERIC_SHEET, {
          side: "bottom",
          title,
          children: <SheetCalendar value={value} onChange={onChange} disableDates={disableDates} captionLayout={captionLayout} startMonth={startMonth} endMonth={endMonth} />
        });
      };
      $[15] = captionLayout;
      $[16] = disableDates;
      $[17] = disabled;
      $[18] = endMonth;
      $[19] = onChange;
      $[20] = openModal;
      $[21] = startMonth;
      $[22] = title;
      $[23] = value;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    const openSheet = t11;
    const t12 = disabled ? "pointer-events-none" : "";
    let t13;
    if ($[25] !== openSheet || $[26] !== t12 || $[27] !== triggerInput) {
      t13 = <div onClick={openSheet} className={t12}>{triggerInput}</div>;
      $[25] = openSheet;
      $[26] = t12;
      $[27] = triggerInput;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    return t13;
  }
  let t11;
  if ($[29] !== triggerInput) {
    t11 = <div>{triggerInput}</div>;
    $[29] = triggerInput;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== disabled || $[32] !== t11) {
    t12 = <PopoverTrigger asChild={true} disabled={disabled} data-sentry-element="PopoverTrigger" data-sentry-source-file="DatePickerInput.tsx">{t11}</PopoverTrigger>;
    $[31] = disabled;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== onChange) {
    t13 = d => {
      onChange(d);
      setPopoverOpen(false);
    };
    $[34] = onChange;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== captionLayout || $[37] !== disableDates || $[38] !== endMonth || $[39] !== startMonth || $[40] !== t13 || $[41] !== value) {
    t14 = <PopoverContent align="start" sideOffset={6} className="w-auto p-0 overflow-hidden" data-sentry-element="PopoverContent" data-sentry-source-file="DatePickerInput.tsx"><PopoverCalendar value={value} onChange={t13} disableDates={disableDates} captionLayout={captionLayout} startMonth={startMonth} endMonth={endMonth} data-sentry-element="PopoverCalendar" data-sentry-source-file="DatePickerInput.tsx" /></PopoverContent>;
    $[36] = captionLayout;
    $[37] = disableDates;
    $[38] = endMonth;
    $[39] = startMonth;
    $[40] = t13;
    $[41] = value;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== popoverOpen || $[44] !== t12 || $[45] !== t14) {
    t15 = <Popover open={popoverOpen} onOpenChange={setPopoverOpen} data-sentry-element="Popover" data-sentry-component="DatePickerInput" data-sentry-source-file="DatePickerInput.tsx">{t12}{t14}</Popover>;
    $[43] = popoverOpen;
    $[44] = t12;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
}

// ── Desktop popover body — no modal context, closes via parent state ────

function PopoverCalendar(t0) {
  const $ = _c(11);
  const {
    value,
    onChange,
    disableDates,
    captionLayout,
    startMonth,
    endMonth
  } = t0;
  const [selected, setSelected] = useState(value);
  const t1 = startMonth ?? DEFAULT_START_MONTH;
  const t2 = endMonth ?? DEFAULT_END_MONTH;
  let t3;
  if ($[0] !== onChange) {
    t3 = date => {
      if (!date) {
        return;
      }
      setSelected(date);
      onChange(date);
    };
    $[0] = onChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== disableDates) {
    t4 = date_0 => disableDates?.(date_0) ?? false;
    $[2] = disableDates;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== captionLayout || $[5] !== selected || $[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <Calendar buttonVariant="outline" mode="single" showOutsideDays={false} fixedWeeks={false} captionLayout={captionLayout} startMonth={t1} endMonth={t2} selected={selected} onSelect={t3} disabled={t4} data-sentry-element="Calendar" data-sentry-component="PopoverCalendar" data-sentry-source-file="DatePickerInput.tsx" />;
    $[4] = captionLayout;
    $[5] = selected;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

// ── Mobile sheet body — closes via ModalProvider ────────────────────────

function SheetCalendar(t0) {
  const $ = _c(12);
  const {
    value,
    onChange,
    disableDates,
    captionLayout,
    startMonth,
    endMonth
  } = t0;
  const {
    closeModal
  } = useModal();
  const [selected, setSelected] = useState(value);
  const t1 = startMonth ?? DEFAULT_START_MONTH;
  const t2 = endMonth ?? DEFAULT_END_MONTH;
  let t3;
  if ($[0] !== closeModal || $[1] !== onChange) {
    t3 = date => {
      if (!date) {
        return;
      }
      setSelected(date);
      onChange(date);
      closeModal();
    };
    $[0] = closeModal;
    $[1] = onChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== disableDates) {
    t4 = date_0 => disableDates?.(date_0) ?? false;
    $[3] = disableDates;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== captionLayout || $[6] !== selected || $[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <Calendar className="w-full h-[calc(100dvw+6px)] sm:h-auto sm:flex justify-center pt-4" size="lg" buttonVariant="outline" mode="single" showOutsideDays={false} fixedWeeks={false} captionLayout={captionLayout} startMonth={t1} endMonth={t2} selected={selected} onSelect={t3} disabled={t4} data-sentry-element="Calendar" data-sentry-component="SheetCalendar" data-sentry-source-file="DatePickerInput.tsx" />;
    $[5] = captionLayout;
    $[6] = selected;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}